import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { buildDepartmentSelectOptionsFromHierarchy } from "@/helpers/salesHierarchyDepartments";
import { isOfflineMode } from "@/helpers/offlineMode";
import type { SalesHierarchyModel } from "@/models/pipeline";
import apiClient, { getPhoenixToken } from "@/services/apiClient";
import { mockWorkers, mockDepartments, mockSalesHierarchy } from "@/services/mockData";
import { pipelineService } from "@/services/PipelineService";
import { Worker, Department, WorkerSelectOption, DepartmentSelectOption } from "@/types/appData";

interface AppDataContextValue {
  workers: Worker[];
  departments: Department[];
  salesHierarchy: SalesHierarchyModel[];
  workerOptions: WorkerSelectOption[];
  departmentOptions: DepartmentSelectOption[];
  currentUser: Worker | null;
  isLoading: boolean;
  refetchWorkers: () => Promise<void>;
}

const AppDataContext = createContext<AppDataContextValue | null>(null);

const extractArray = <T,>(data: unknown): T[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && "data" in data) {
    return extractArray((data as { data: unknown }).data);
  }
  return [];
};

export const AppDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [salesHierarchy, setSalesHierarchy] = useState<SalesHierarchyModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const offlineMode = isOfflineMode();

  const fetchWorkers = useCallback(async () => {
    const response = await apiClient.get("/twilio-flex-helpers/phoenix-user-list");
    setWorkers(extractArray<Worker>(response.data));
  }, []);

  useEffect(() => {
    if (offlineMode) {
      setWorkers(mockWorkers as Worker[]);
      setDepartments(mockDepartments as Department[]);
      setSalesHierarchy(mockSalesHierarchy);
      setIsLoading(false);
      return;
    }

    if (!isAuthenticated) return;

    const fetchData = async () => {
      const token = getPhoenixToken();
      if (!token) {
        setTimeout(fetchData, 100);
        return;
      }

      try {
        const [, departmentsResponse, hierarchyData] = await Promise.all([
          fetchWorkers(),
          apiClient.get("/twilio-flex-helpers/phoenix-departments-list"),
          pipelineService.getSalesHierarchy().catch((err) => {
            console.error("Failed to fetch sales hierarchy:", err);
            return [] as SalesHierarchyModel[];
          }),
        ]);

        setDepartments(extractArray<Department>(departmentsResponse.data));
        setSalesHierarchy(hierarchyData);
      } catch (error) {
        console.error("Failed to fetch app data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, offlineMode, fetchWorkers]);

  const workerOptions = useMemo<WorkerSelectOption[]>(
    () =>
      workers
        .filter((worker) => worker?.userId != null && worker?.name != null)
        .map((worker) => ({
          value: worker.userId,
          label: worker.name,
        })),
    [workers]
  );

  const departmentOptions = useMemo<DepartmentSelectOption[]>(
    () => buildDepartmentSelectOptionsFromHierarchy(salesHierarchy, departments),
    [salesHierarchy, departments]
  );

  const currentUser = useMemo(() => {
    if (offlineMode) {
      return workers[0] ?? null;
    }

    if (!accounts.length || !workers.length) return null;
    const userEmail = accounts[0]?.username?.toLowerCase();
    if (!userEmail) return null;
    return workers.find((worker) => worker.email?.toLowerCase() === userEmail) ?? null;
  }, [accounts, workers, offlineMode]);

  return (
    <AppDataContext.Provider
      value={{
        workers,
        departments,
        salesHierarchy,
        workerOptions,
        departmentOptions,
        currentUser,
        isLoading,
        refetchWorkers: fetchWorkers,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
};

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error("useAppData must be used within an AppDataProvider");
  }
  return context;
};
